// client/src/hooks/useAuth.js - VERSÃO 100% COMPLETA E CORRIGIDA (COM REFRESH TOKEN)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAlert from './useAlert';

// Exportamos a chave de query para que outros hooks possam usá-la
export const ME_QUERY_KEY = ['me'];

// Função auxiliar para armazenar token
const storeToken = (token, rememberMe = true) => {
    if (rememberMe) {
        localStorage.setItem('token', token);
    } else {
        sessionStorage.setItem('token', token);
    }
};

// ✅ NOVO: Função para armazenar refresh token
const storeRefreshToken = (refreshToken, rememberMe = true) => {
    if (rememberMe) {
        localStorage.setItem('refreshToken', refreshToken);
    } else {
        sessionStorage.setItem('refreshToken', refreshToken);
    }
};

// Função auxiliar para recuperar token
const getStoredToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// ✅ ACHADO CRÍTICO: decodifica só o payload do JWT (sem verificar
// assinatura — é só uma checagem de consistência local, a validação de
// verdade sempre acontece no servidor) pra comparar contra o "user" salvo
// no localStorage antes de confiar nele como initialData. Sem isso,
// useQuery({ initialData: loadUserData() }) considera os dados "frescos"
// (dentro do staleTime de 60s) e NUNCA chama /auth/me no mount — mesmo
// quando o token mudou (login como outro usuário, fim de impersonate,
// token de sessão anterior sobrevivendo). O app roda inteiro com
// escopo/permissões de OUTRA pessoa até o refetchInterval de 2min bater,
// causando redirecionamentos incorretos pra /acesso-negado ou /login.
const decodeJwtId = (token) => {
    try {
        const payload = token.split('.')[1];
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json?.id || null;
    } catch {
        return null;
    }
};

// ✅ NOVO: Função para recuperar refresh token
const getStoredRefreshToken = () => {
    return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
};

// Função auxiliar para limpar tokens
const clearTokens = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('matriz_selected_account');
    localStorage.removeItem('selected_account_id');
};

// ✅ NOVO: Função para salvar dados do usuário
const saveUserData = (userData) => {
    if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        
        // ✅ Salvar accountId separadamente para fácil acesso
        if (userData.accountId) {
            localStorage.setItem('selected_account_id', userData.accountId);
        }
        
        // ✅ Para Matriz Admin, salvar conta selecionada
        if (userData.scope === 'matriz' && userData.accountId) {
            localStorage.setItem('matriz_selected_account', userData.accountId);
        }
    }
};

// ✅ NOVO: Função para carregar dados do usuário
// ✅ CORREÇÃO: recebe o token atual e rejeita o cache se o _id nele
// codificado não bater com o _id do token — ver decodeJwtId acima.
const loadUserData = (currentToken) => {
    try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;
        const user = JSON.parse(userStr);
        // Rejeitar dados corrompidos: sem _id, sem role, ou sem permissions (estrutura antiga)
        if (!user || !user._id || !user.role || !Array.isArray(user.permissions)) {
            console.warn('[useMe] Dados do localStorage inválidos ou desatualizados, aguardando /auth/me');
            localStorage.removeItem('user');
            return null;
        }
        // Cache de um usuário/token anterior — descarta e força /auth/me
        if (currentToken) {
            const tokenUserId = decodeJwtId(currentToken);
            if (tokenUserId && String(tokenUserId) !== String(user._id)) {
                console.warn('[useMe] Cache de usuário não corresponde ao token atual, descartando e aguardando /auth/me');
                localStorage.removeItem('user');
                return null;
            }
        }
        return user;
    } catch (error) {
        console.error('❌ Erro ao carregar dados do usuário:', error);
        localStorage.removeItem('user');
        return null;
    }
};

// ✅ NOVO: Intervalo para auto-refresh do token
let refreshInterval = null;

const startAutoRefresh = (callback, intervalMs = 4 * 60 * 1000) => {
    if (refreshInterval) clearInterval(refreshInterval);
    refreshInterval = setInterval(() => {
        const token = getStoredToken();
        const refreshToken = getStoredRefreshToken();
        if (token && refreshToken) {
            console.log('🔄 [AUTH] Auto-refresh token...');
            callback().catch(err => console.error('❌ Auto-refresh falhou:', err));
        }
    }, intervalMs);
    return refreshInterval;
};

const stopAutoRefresh = () => {
    if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
    }
};

/**
 * @desc Hook para buscar os dados do usuário logado (/auth/me)
 * ✅ 100% COMPATÍVEL com nova estrutura de relacionamentos
 */
export const useMe = () => {
    const token = getStoredToken();

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ME_QUERY_KEY,
        queryFn: async () => {
            if (!token) {
                throw new Error('Token não disponível');
            }

            try {
                console.log('🔐 [useMe] Buscando dados do usuário...');
                const res = await api.get('/auth/me');
                
                // ✅ CORREÇÃO: Processar dados para compatibilidade com relacionamentos
                const responseData = res.data.data || res.data;
                
                console.log('🔐 [useMe] Dados completos recebidos:', {
                    hasData: !!responseData,
                    hasUser: !!responseData.user,
                    hasAccounts: !!(responseData.accounts && responseData.accounts.length),
                    hasFranquias: !!(responseData.franquias && responseData.franquias.length)
                });
                
                // Extrair dados do usuário
                const userData = responseData.user || responseData;
                
                // ✅ CORREÇÃO: Buscar conta principal dos relacionamentos
                let mainAccount = null;
                let mainAccountRelationship = null;
                let mainFranquia = null;
                
                if (responseData.accounts && responseData.accounts.length > 0) {
                    // Encontrar a conta principal (ativa ou a primeira)
                    mainAccountRelationship = responseData.accounts.find(acc => 
                        acc.status === 'ativo' && 
                        acc.account?.status === 'active'
                    ) || responseData.accounts[0];
                    
                    if (mainAccountRelationship) {
                        mainAccount = mainAccountRelationship.account;
                        console.log('✅ [useMe] Conta principal encontrada:', {
                            accountId: mainAccount?._id,
                            accountName: mainAccount?.name,
                            relationshipId: mainAccountRelationship._id
                        });
                    }
                }
                
                // ✅ CORREÇÃO: Buscar franquia principal
                if (responseData.franquias && responseData.franquias.length > 0) {
                    const activeFranquia = responseData.franquias.find(f => 
                        f.status === 'ativo' && 
                        f.franquia?.status === 'active'
                    ) || responseData.franquias[0];
                    
                    if (activeFranquia) {
                        mainFranquia = activeFranquia.franquia;
                        console.log('✅ [useMe] Franquia principal encontrada:', {
                            franquiaId: mainFranquia?._id,
                            franquiaName: mainFranquia?.nome
                        });
                    }
                }
                
                // ✅ CORREÇÃO CRÍTICA: Garantir que accountId esteja disponível
                const accountId = mainAccount?._id || mainAccount?.id || 
                                 responseData.currentAccount?._id || 
                                 responseData.currentAccount?.id;
                
                // ✅ CORREÇÃO: Montar objeto de usuário compatível
                const processedUser = {
                    // Dados básicos do usuário
                    ...userData,
                    _id: userData._id || userData.id,
                    id: userData.id || userData._id,
                    
                    // ✅ CORREÇÃO: Adicionar conta principal ao objeto user
                    account: mainAccount || responseData.currentAccount || responseData.account,
                    accountRelationship: mainAccountRelationship,
                    
                    // ✅ CORREÇÃO CRÍTICA: Adicionar accountId direto no user para compatibilidade
                    accountId: accountId,
                    
                    // ✅ Adicionar franquia principal
                    franquia: mainFranquia || responseData.franquia || responseData.currentFranquia,
                    franquiaId: mainFranquia?._id || responseData.franquia?._id || responseData.currentFranquia?._id,
                    
                    // Preservar arrays de relacionamentos
                    accounts: responseData.accounts || [],
                    vinculos: responseData.accounts || [], // Para compatibilidade
                    
                    // ✅ CORREÇÃO: Preservar franquias se existirem
                    franquias: responseData.franquias || [],
                    allFranquias: responseData.allFranquias || [],
                    
                    // Informações atuais
                    currentAccount: responseData.currentAccount || mainAccount,
                    currentRole: responseData.currentRole || mainAccountRelationship?.role || userData.role,
                    currentFranquia: responseData.currentFranquia || mainFranquia,
                    clienteInfo: responseData.clienteInfo,
                    
                    // ✅ CORREÇÃO: Garantir que permissions existem
                    permissions: userData.permissions || userData.role?.permissions || [],
                    
                    // ✅ CORREÇÃO: Garantir que requirePasswordChange está disponível
                    requirePasswordChange: userData.requirePasswordChange !== undefined 
                        ? userData.requirePasswordChange 
                        : false,
                    
                    // ✅ NOVO: Informações de hierarquia
                    scope: userData.scope || userData.role?.scope || 'conta',
                    adminType: userData.adminType || userData.role?.name,
                    hasFullAccess: userData.hasFullAccess || false
                };
                
                console.log('✅ [useMe] Usuário processado:', {
                    userId: processedUser._id,
                    email: processedUser.email,
                    scope: processedUser.scope,
                    hasAccount: !!processedUser.account,
                    accountId: processedUser.accountId,
                    accountsCount: processedUser.accounts?.length,
                    franquiasCount: processedUser.franquias?.length,
                    requirePasswordChange: processedUser.requirePasswordChange,
                    permissionsCount: processedUser.permissions?.length
                });
                
                // ✅ Salvar dados no localStorage para uso rápido
                saveUserData(processedUser);
                
                return processedUser;
            } catch (err) {
                console.error('❌ [useMe] Erro ao buscar dados:', err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    // Token inválido ou sem permissão, limpar storage
                    clearTokens();
                }
                throw err;
            }
        },
        // ✅ Reduzido de 5min pra 60s + refetchOnWindowFocus ligado (só aqui —
        // o QueryClient global desliga isso por padrão). Sem isso, dados
        // sensíveis a mudança fora da própria sessão (plano/features,
        // permissões, status da conta) podiam ficar presos por tempo
        // indefinido: staleTime sozinho não dispara refetch em background,
        // só marca "stale" — sem alguma dessas triggers, uma aba já aberta
        // nunca buscava de novo. Caso real: matriz habilita uma feature de
        // plano pra uma conta, e o admin da conta só via a mudança refletir
        // depois de deslogar e logar de novo.
        staleTime: 1000 * 60, // 60 segundos
        refetchOnWindowFocus: true,
        // Cobre o caso de uma aba ficar aberta e em foco continuamente (sem
        // trocar de aba pra disparar o refetchOnWindowFocus acima) — endpoint
        // leve, intervalo generoso o bastante pra não pesar no servidor.
        refetchInterval: 1000 * 60 * 2, // 2 minutos
        refetchIntervalInBackground: false,
        retry: (failureCount, error) => {
            // Não tentar novamente para erros 401/403
            if (error.response?.status === 401 || error.response?.status === 403) {
                return false;
            }
            return failureCount < 2;
        },
        enabled: !!token, // Só executa se existir token
        // ✅ ACHADO CRÍTICO #2: `|| undefined` aqui não é estético — é o que
        // faz a query realmente rodar. React Query só trata initialData como
        // "ausente" quando o valor é `undefined`; `null` conta como dado
        // válido (status vira 'success' na hora, isLoading:false, data:null,
        // e dataUpdatedAt fica "agora" — dentro do staleTime de 60s, então
        // NENHUM refetch dispara, nem no mount). Como loadUserData() retorna
        // `null` sempre que não há cache/o cache não bate com o token (login
        // "a frio", fim de impersonate, etc.), sem esse `|| undefined` a
        // query nunca chamava /auth/me nesse cenário: ficava presa com
        // user:null só que "carregado", e PrivateRoute (isAuthenticated =
        // !!user) mandava direto pra /login mesmo com token válido.
        initialData: loadUserData(token) || undefined
    });

    // ✅ NOVO: Iniciar auto-refresh quando o usuário estiver logado
    useEffect(() => {
        if (data && data._id) {
            const refresh = async () => {
                try {
                    const refreshToken = getStoredRefreshToken();
                    if (refreshToken) {
                        const response = await api.post('/auth/refresh-token', { refreshToken });
                        const { token, refreshToken: newRefreshToken } = response.data;
                        if (token) {
                            storeToken(token, true);
                            if (newRefreshToken) storeRefreshToken(newRefreshToken, true);
                            console.log('✅ Auto-refresh token realizado com sucesso');
                        }
                    }
                } catch (error) {
                    console.error('❌ Auto-refresh token falhou:', error);
                }
            };
            startAutoRefresh(refresh, 4 * 60 * 1000);
            return () => stopAutoRefresh();
        }
    }, [data]);

    // ✅ CORREÇÃO: hasPermission com hierarquia de escopos (CORRIGIDA)
    const hasPermission = useCallback((permission) => {
        if (!data || !Array.isArray(data.permissions)) {
            return false;
        }
        
        const userPermissions = data.permissions;
        
        // 1. SUPER ADMIN (bypass total)
        if (userPermissions.includes('super_admin:bypass_all_checks')) {
            return true;
        }
        
        // 2. MATRIZ ADMIN (acesso total a tudo)
        if (userPermissions.includes('matriz_admin:full_access')) {
            return true;
        }
        
        // 3. FRANQUIA ADMIN: acesso total a recursos da franquia e contas
        if (userPermissions.includes('franquia_admin:full_access')) {
            // Permissões de matriz e super_admin são bloqueadas
            if (permission.startsWith('matriz_') || permission === 'super_admin:bypass_all_checks') {
                return false;
            }
            return true;
        }
        
        // 4. CONTA ADMIN: acesso total a recursos da conta, mas não a franquia ou matriz
        if (userPermissions.includes('conta_admin:full_access')) {
            // Permissões de matriz, franquia e super_admin são bloqueadas
            if (permission.startsWith('matriz_') || 
                permission.startsWith('franquia_') || 
                permission === 'super_admin:bypass_all_checks') {
                return false;
            }
            return true;
        }
        
        // 5. Verificação granular (permissão exata)
        return userPermissions.includes(permission);
    }, [data]);

    // ✅ CORREÇÃO: Helper para obter conta atual (compatibilidade)
    const getCurrentAccount = useCallback(() => {
        if (!data) return null;
        
        // Prioridade: currentAccount > account > primeira conta ativa
        return data.currentAccount || 
               data.account || 
               (data.accounts && data.accounts[0]?.account) || 
               null;
    }, [data]);

    // ✅ CORREÇÃO: Helper para obter role atual
    const getCurrentRole = useCallback(() => {
        if (!data) return null;
        
        return data.currentRole || 
               data.role || 
               (data.accounts && data.accounts[0]?.role) || 
               null;
    }, [data]);

    // ✅ CORREÇÃO: Helper para verificar se é profissional
    const isProfessional = useCallback(() => {
        return !!(data?.profissional?.isProfissional);
    }, [data]);

    // ✅ NOVO: Helper para obter ID da conta (para uso direto)
    const getAccountId = useCallback(() => {
        return data?.accountId || 
               data?.account?._id || 
               data?.account?.id || 
               localStorage.getItem('selected_account_id') ||
               localStorage.getItem('matriz_selected_account') ||
               null;
    }, [data]);

    // ✅ NOVO: Verificar se usuário tem conta
    const hasAccount = useCallback(() => {
        return !!getAccountId();
    }, [getAccountId]);

    // ✅ NOVO: Verificar se usuário precisa trocar senha
    const needsPasswordChange = useCallback(() => {
        return data?.requirePasswordChange === true;
    }, [data]);

    // ✅ NOVO: Verificar se usuário tem franquia
    const hasFranquia = useCallback(() => {
        return !!(data?.franquia || (data?.franquias && data.franquias.length > 0));
    }, [data]);

    // ✅ NOVO: Obter franquia atual
    const getCurrentFranquia = useCallback(() => {
        if (!data) return null;
        
        return data.franquia || 
               data.currentFranquia || 
               (data.franquias && data.franquias[0]?.franquia) || 
               null;
    }, [data]);

    // ✅ NOVO: Obter conta selecionada para Matriz Admin
    const getSelectedAccountId = useCallback(() => {
        if (data?.scope === 'matriz') {
            return localStorage.getItem('matriz_selected_account') || getAccountId();
        }
        return getAccountId();
    }, [data, getAccountId]);

    // ✅ NOVO: Definir conta selecionada para Matriz Admin
    const setSelectedAccountId = useCallback((accountId) => {
        if (data?.scope === 'matriz' && accountId) {
            localStorage.setItem('matriz_selected_account', accountId);
            localStorage.setItem('selected_account_id', accountId);
            return true;
        }
        return false;
    }, [data]);

    // ✅ NOVO: Obter todas as contas disponíveis
    const getAvailableAccounts = useCallback(() => {
        if (!data) return [];
        
        // Para Matriz Admin, retorna todas as contas ativas
        if (data.scope === 'matriz') {
            return data.accounts || [];
        }
        
        // Para outros usuários, retorna apenas contas vinculadas
        return data.accounts?.filter(acc => acc.status === 'ativo') || [];
    }, [data]);

    return {
        user: data,
        isLoading: isLoading && !!token, // Só mostra loading se houver token
        isError,
        error,
        hasPermission,
        refetch,
        
        // ✅ HELPERS CORRIGIDOS
        getCurrentAccount,
        getCurrentRole,
        isProfessional,
        
        // ✅ NOVOS HELPERS PARA FACILITAR O USO
        getAccountId,
        hasAccount,
        needsPasswordChange,
        hasFranquia,
        getCurrentFranquia,
        
        // ✅ NOVOS HELPERS PARA MATRIZ ADMIN
        getSelectedAccountId,
        setSelectedAccountId,
        getAvailableAccounts,
        
        // ✅ COMPATIBILIDADE: accountId direto (para não quebrar código existente)
        accountId: getAccountId(),
        
        // ✅ Informações do escopo
        scope: data?.scope || 'conta',
        isMatrizAdmin: data?.scope === 'matriz' || data?.adminType === 'matriz_admin',
        isFranquiaAdmin: data?.scope === 'franquia' || data?.adminType === 'franquia_admin',
        isContaAdmin: data?.scope === 'conta' || data?.adminType === 'conta_admin'
    };
};

/**
 * @desc Hook que agrupa todas as mutações relacionadas à autenticação
 */
export const useAuthMutations = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setAlert } = useAlert();

    const invalidateAndReloadUser = async () => {
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    };

    // ✅ ATUALIZADO: Login para lidar com novo formato de resposta
    const login = useMutation({
        mutationFn: ({ email, telefone, password, rememberMe = true }) => 
            api.post('/auth/login', { email, telefone, password }).then(response => {
                return { ...response, rememberMe };
            }),
        onSuccess: (response, variables) => {
            // O servidor retorna: { token, data: { user, accounts, franquias }, franquia, allFranquias }
            const { token, refreshToken, data: responseData, franquia, allFranquias } = response.data;
            const { user, accounts = [], franquias = [] } = responseData || {};
            const { rememberMe } = variables;

            // Armazenar token conforme preferência do usuário
            storeToken(token, rememberMe);

            if (refreshToken) {
                storeRefreshToken(refreshToken, rememberMe);
            }

            const mainAccount = accounts.length > 0 ? accounts[0].account : null;
            const mainRole = accounts.length > 0 ? accounts[0].role : null;
            const mainFranquia = franquia || (franquias.length > 0 ? franquias[0].franquia : null);

            const userData = {
                ...user,
                accounts,
                franquias,
                account: mainAccount,
                accountId: mainAccount?._id,
                accountRelationship: accounts.length > 0 ? accounts[0] : null,
                franquia: mainFranquia,
                allFranquias: allFranquias || [],
                currentAccount: mainAccount,
                currentRole: mainRole,
                currentFranquia: mainFranquia,
                permissions: user?.permissions || user?.role?.permissions || [],
                scope: user?.scope || user?.role?.scope || 'conta',
                adminType: user?.adminType
            };

            // ✅ O painel web é exclusivo pra matriz/franquia — usuários de
            // Conta usam o app mobile (admin.situai.site). Bloqueado aqui
            // (não no backend, que é o mesmo endpoint pra web e mobile)
            // porque é uma divisão de produto entre as duas frentes, não
            // uma fronteira de permissão de dados: o próprio usuário já
            // teria acesso legítimo aos mesmos dados por qualquer uma das
            // duas pontas.
            if (userData.scope === 'conta') {
                clearTokens();
                setAlert('Este painel é exclusivo para administradores de matriz/franquia. Contas devem acessar pelo aplicativo mobile.', 'danger');
                return;
            }

            saveUserData(userData);
            queryClient.setQueryData(ME_QUERY_KEY, userData);

            setAlert('Login realizado com sucesso!', 'success');

            if (user?.requirePasswordChange) {
                navigate('/force-change-password', { replace: true });
            } else {
                const urlParams = new URLSearchParams(window.location.search);
                const redirectUrl = urlParams.get('redirect') || '/';
                navigate(redirectUrl, { replace: true });
            }
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Credenciais inválidas';
            setAlert(errorMessage, 'danger');
        }
    });

    // ✅ Mutation para força de troca de senha
    const forceUpdatePassword = useMutation({
        mutationFn: ({ newPassword }) => {
            console.log('🔄 [forceUpdatePassword] Chamando /auth/force-updatepassword');
            return api.put('/auth/force-updatepassword', { newPassword });
        },
        onSuccess: (response) => {
            console.log('✅ [forceUpdatePassword] Senha forçada atualizada:', response);
            
            const { token, refreshToken, data: userData, franquia, allFranquias } = response.data;
            
            // Armazenar novo token
            storeToken(token, true);
            if (refreshToken) storeRefreshToken(refreshToken, true);
            
            // Processar dados do usuário
            const processedUserData = {
                ...userData,
                requirePasswordChange: false, // ✅ Importante: marcar como já alterada
                account: userData.account || null,
                accountId: userData.account?._id || null,
                accounts: userData.accounts || [],
                franquias: userData.franquias || [],
                franquia: franquia || userData.franquia || null,
                allFranquias: allFranquias || userData.allFranquias || [],
                permissions: userData.permissions || userData.role?.permissions || []
            };
            
            // ✅ Salvar dados atualizados
            saveUserData(processedUserData);
            
            // Atualizar cache do React Query
            queryClient.setQueryData(ME_QUERY_KEY, processedUserData);
            
            setAlert('Senha definida com sucesso! Redirecionando...', 'success', 3000);
            
            // Redirecionar após 3 segundos
            setTimeout(() => {
                navigate('/', { replace: true });
                window.location.reload();
            }, 3000);
        },
        onError: (err) => {
            console.error('❌ [forceUpdatePassword] Erro:', err);
            const errorMessage = err.response?.data?.error || 'Erro ao definir nova senha';
            setAlert(errorMessage, 'danger');
        },
    });

    // ✅ MUTATIONS PARA ATUALIZAÇÃO DO PRÓPRIO PERFIL
    const updateMyDetails = useMutation({
        mutationFn: (userData) => {
            console.log('🔄 Enviando para /auth/updatedetails:', userData);
            return api.put('/auth/updatedetails', userData);
        },
        onSuccess: (response) => {
            console.log('✅ Perfil atualizado com sucesso:', response);
            const userData = response.data.data || response.data;
            saveUserData(userData);
            queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            setAlert('Perfil atualizado com sucesso!', 'success');
        },
        onError: (err) => {
            console.error('❌ Erro ao atualizar perfil:', err);
            const errorMessage = err.response?.data?.error || 'Erro ao atualizar perfil';
            setAlert(errorMessage, 'danger');
        },
    });

    const updateMyPhoto = useMutation({
        mutationFn: (file) => {
            console.log('🔄 Enviando foto para /auth/updatephoto');
            const formData = new FormData();
            formData.append('file', file);
            return api.put('/auth/updatephoto', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        },
        onSuccess: (response) => {
            console.log('✅ Foto atualizada com sucesso:', response);
            queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            setAlert('Foto atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error('❌ Erro ao atualizar foto:', err);
            const errorMessage = err.response?.data?.error || 'Erro ao atualizar foto';
            setAlert(errorMessage, 'danger');
        },
    });

    const updateMyPassword = useMutation({
        mutationFn: (passwordData) => {
            console.log('🔄 Atualizando senha via /auth/updatepassword');
            return api.put('/auth/updatepassword', passwordData);
        },
        onSuccess: (data) => {
            console.log('✅ Senha atualizada com sucesso:', data);
            setAlert('Senha atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error('❌ Erro ao atualizar senha:', err);
            const errorMessage = err.response?.data?.error || 'Erro ao atualizar senha';
            setAlert(errorMessage, 'danger');
        },
    });

    const logout = useMutation({
        mutationFn: () => api.get('/auth/logout'),
        onSuccess: () => {
            stopAutoRefresh();
            clearTokens();
            setAlert('Logout realizado com sucesso!', 'success');
            queryClient.clear();
            navigate('/login');
        },
        onError: (err) => {
            // Limpar storage mesmo em caso de erro
            stopAutoRefresh();
            clearTokens();
            queryClient.clear();
            navigate('/login');
            setAlert(err.response?.data?.error || 'Erro ao fazer logout', 'danger');
        }
    });

    const impersonateUser = useMutation({
        // ✅ useHandoff:true sinaliza que o alvo, se for conta_admin, deve
        // vir como código de handoff (não como token direto) — ver ramo
        // correspondente em authController.impersonateUser. Mobile nunca
        // manda essa flag (não tem domínio separado pra Conta), então
        // sempre recebe o token direto, mesmo fluxo de antes.
        //
        // ✅ accountId/franquiaId — o admin sempre clica "Entrar como" numa
        // LINHA específica de uma tabela (uma Conta ou uma Franquia), então
        // manda explicitamente qual delas. Sem isso, o backend tinha que
        // adivinhar (pegava o primeiro vínculo ativo do usuário-alvo) — se
        // esse alvo administra mais de uma conta/franquia, a concessão de
        // acesso de suporte podia ser checada contra a conta ERRADA (uma
        // sem concessão bloqueava indevidamente, ou pior, uma com concessão
        // liberava uma sessão que também enxerga as outras contas do mesmo
        // usuário, nenhuma delas tendo de fato concedido acesso).
        // Aceita tanto a chamada antiga (`mutate(userId)`, string) quanto a
        // nova (`mutate({ userId, accountId })`) — retrocompatível com
        // qualquer call site que ainda não foi atualizado.
        mutationFn: (params) => {
            const { userId, accountId, franquiaId } = typeof params === 'string' ? { userId: params } : (params || {});
            return api.post('/auth/impersonate', { targetUserId: userId, accountId, franquiaId, useHandoff: true });
        },
        onSuccess: (response) => {
            // ✅ HANDOFF ENTRE DOMÍNIOS — matriz/franquia ficam em
            // admin.situai.com.br; suporte numa Conta abre em
            // admin.situai.site (domínio separado, sem localStorage
            // compartilhado). A aba atual NÃO troca de token nem recarrega
            // — o admin continua logado exatamente como estava. A nova aba
            // troca o código pelo token de verdade em /auth/handoff (ver
            // AuthHandoffPage.js) — o token real nunca trafega pela URL.
            if (response.data.handoff) {
                const baseUrl = process.env.REACT_APP_CONTA_APP_URL || window.location.origin;
                window.open(`${baseUrl}/auth/handoff?code=${response.data.handoffCode}`, '_blank', 'noopener,noreferrer');
                setAlert('Uma nova guia foi aberta com o acesso de suporte à conta.', 'success');
                return;
            }

            const { token, refreshToken, data: userData } = response.data;

            // ✅ Guarda a sessão do admin ANTES de sobrescrever — não existe
            // revogação de token no backend pra sessões normais, então
            // "Encerrar acesso" (ver endImpersonation) restaura este token
            // em vez de exigir login de novo. sessionStorage (não
            // localStorage) porque só faz sentido durar a aba/sessão atual.
            const adminToken = getStoredToken();
            const adminRefreshToken = getStoredRefreshToken();
            if (adminToken) {
                sessionStorage.setItem('adminSessionAntesDeSuporte', JSON.stringify({
                    token: adminToken,
                    refreshToken: adminRefreshToken || null
                }));
            }

            clearTokens();
            storeToken(token, false);
            if (refreshToken) storeRefreshToken(refreshToken, false);

            // ✅ CORREÇÃO: Processar dados para compatibilidade com relacionamentos
            const processedUserData = {
                ...userData,
                account: userData.currentAccount || (userData.accounts && userData.accounts[0]?.account),
                accountId: userData.currentAccount?._id || (userData.accounts && userData.accounts[0]?.account?._id),
                accounts: userData.accounts || [],
                permissions: userData.permissions || userData.role?.permissions || [],
                scope: userData.scope || userData.role?.scope || 'conta'
            };

            // ✅ Salvar dados
            saveUserData(processedUserData);

            queryClient.setQueryData(ME_QUERY_KEY, processedUserData);

            setAlert(`Sessão iniciada como ${processedUserData.name}!`, 'success');

            // ✅ Sem invalidateQueries() aqui de propósito — o reload logo
            // abaixo já busca tudo do zero; invalidar antes dele dispara
            // refetch das queries AINDA montadas (ex.: a lista de Contas da
            // tela de onde veio o clique) usando o token novo (escopo
            // conta), o que sempre falhava com 403 num componente que só
            // matriz/franquia podem ver — ruído visível bem no instante da
            // troca, sem nenhum efeito útil já que a página inteira recarrega.
            navigate('/', { replace: true });
            window.location.reload();
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao tentar iniciar a sessão.';
            setAlert(errorMessage, 'danger');
        }
    });

    // ✅ Encerra a sessão de impersonação e restaura a sessão do admin
    // guardada em sessionStorage por impersonateUser.onSuccess. Não chama
    // API nenhuma — não existe revogação de token no backend pra sessões
    // normais (só a SupportAccessGrant do tenant é revogável, via
    // useSupportAccess), então "encerrar" é puramente local.
    const endImpersonation = useCallback(() => {
        const stashed = sessionStorage.getItem('adminSessionAntesDeSuporte');
        if (!stashed) {
            setAlert('Nenhuma sessão de administrador para restaurar. Faça login novamente.', 'danger');
            navigate('/login');
            return;
        }

        try {
            const { token, refreshToken } = JSON.parse(stashed);
            clearTokens();
            sessionStorage.removeItem('adminSessionAntesDeSuporte');
            if (token) storeToken(token, false);
            if (refreshToken) storeRefreshToken(refreshToken, false);

            queryClient.clear();
            navigate('/', { replace: true });
            window.location.reload();
        } catch (err) {
            console.error('❌ Erro ao restaurar sessão do admin:', err);
            sessionStorage.removeItem('adminSessionAntesDeSuporte');
            setAlert('Não foi possível restaurar a sessão anterior. Faça login novamente.', 'danger');
            navigate('/login');
        }
    }, [navigate, queryClient, setAlert]);

    const forgotPassword = useMutation({
        mutationFn: ({ email, telefone }) => api.post('/auth/forgotpassword', email ? { email } : { telefone }),
        onSuccess: (data) => {
            setAlert(data.message || 'Instruções de recuperação enviadas com sucesso!', 'success');
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao enviar email de recuperação';
            setAlert(errorMessage, 'danger');
        }
    });

    const resetPassword = useMutation({
        mutationFn: ({ resetToken, password }) =>
            api.put(`/auth/resetpassword/${resetToken}`, { password }),
        onSuccess: (response) => {
            // PUT /auth/resetpassword/:token já devolve um JWT válido
            // (sendTokenResponse) — login automático em vez de mandar o
            // usuário digitar a senha que acabou de definir de novo.
            const { token } = response.data || {};
            if (token) {
                storeToken(token, true);
                queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            }
            setAlert('Senha redefinida com sucesso!', 'success');
            navigate('/', { replace: true });
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao redefinir senha';
            setAlert(errorMessage, 'danger');
        }
    });

    // ✅ Valida o token de reset (link recebido por email/SMS/WhatsApp) antes
    // de mostrar o formulário de nova senha — GET /auth/resetpassword/:resettoken
    const validateResetToken = useMutation({
        mutationFn: (resetToken) => api.get(`/auth/resetpassword/${resetToken}`)
    });

    const verifyEmail = useMutation({
        mutationFn: (verificationToken) => 
            api.post('/auth/verifyemail', { verificationToken }),
        onSuccess: (data) => {
            setAlert(data.message || 'Email verificado com sucesso!', 'success');
            queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao verificar email';
            setAlert(errorMessage, 'danger');
        }
    });

    const resendVerificationEmail = useMutation({
        mutationFn: () => api.post('/auth/resendverification'),
        onSuccess: (data) => {
            setAlert(data.message || 'Email de verificação reenviado!', 'success');
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao reenviar email de verificação';
            setAlert(errorMessage, 'danger');
        }
    });

    // ✅ NOVA MUTATION: Refresh token
    const refreshToken = useMutation({
        mutationFn: async () => {
            const refreshToken = getStoredRefreshToken();
            if (!refreshToken) throw new Error('Refresh token não disponível');
            const response = await api.post('/auth/refresh-token', { refreshToken });
            return response.data;
        },
        onSuccess: (response) => {
            const { token, refreshToken: newRefreshToken } = response;
            if (token) {
                storeToken(token, true);
                if (newRefreshToken) storeRefreshToken(newRefreshToken, true);
                queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
                console.log('✅ Token renovado com sucesso');
            }
        },
        onError: (err) => {
            console.error('❌ Erro ao refresh token:', err);
            if (err.response?.status === 401) {
                stopAutoRefresh();
                clearTokens();
                navigate('/login?session=expired');
            }
        }
    });

    return {
        // ✅ MUTATIONS PRINCIPAIS
        login,
        logout,
        impersonateUser,
        endImpersonation,
        refreshToken,
        
        // ✅ ATUALIZAÇÕES DE PERFIL
        updateMyDetails,
        updateMyPhoto,
        updateMyPassword,
        forceUpdatePassword,
        
        // ✅ RECUPERAÇÃO DE SENHA
        forgotPassword,
        resetPassword,
        validateResetToken,
        
        // ✅ VERIFICAÇÃO DE EMAIL
        verifyEmail,
        resendVerificationEmail,
        
        // ✅ UTILITÁRIOS
        invalidateAndReloadUser,
        clearTokens,
        saveUserData
    };
};

/**
 * @desc Hook principal de autenticação que combina queries e mutations
 */
export const useAuth = () => {
    const me = useMe();
    const mutations = useAuthMutations();
    
    return {
        ...me,
        ...mutations,
        isAuthenticated: !!me.user && !!getStoredToken(),
        token: getStoredToken()
    };
};

/**
 * @desc Hook para verificar autenticação em componentes protegidos
 */
export const useAuthGuard = (requiredPermission = null) => {
    const { user, isLoading, isAuthenticated, hasPermission } = useAuth();
    
    const hasRequiredPermission = requiredPermission ? hasPermission(requiredPermission) : true;
    const shouldRedirect = !isLoading && (!isAuthenticated || !hasRequiredPermission);
    const canAccess = !isLoading && isAuthenticated && hasRequiredPermission;
    
    return {
        user,
        isLoading,
        isAuthenticated,
        hasRequiredPermission,
        shouldRedirect,
        canAccess,
        redirectPath: !isAuthenticated ? '/login' : '/unauthorized'
    };
};

/**
 * @desc Hook para verificar permissões específicas
 */
export const usePermission = (requiredPermission) => {
    const { hasPermission, isLoading, user } = useAuth();
    
    return {
        hasPermission: hasPermission(requiredPermission),
        isLoading,
        canAccess: !isLoading && hasPermission(requiredPermission),
        userScope: user?.scope,
        userPermissions: user?.permissions
    };
};

/**
 * @desc Hook para verificar se o usuário é administrador
 */
export const useIsAdmin = () => {
    const { user, hasPermission } = useAuth();
    
    const isMatrizAdmin = hasPermission('matriz_admin:full_access') || user?.scope === 'matriz';
    const isFranquiaAdmin = hasPermission('franquia_admin:full_access') || user?.scope === 'franquia';
    const isContaAdmin = hasPermission('conta_admin:full_access') || user?.scope === 'conta';
    
    return {
        isMatrizAdmin,
        isFranquiaAdmin,
        isContaAdmin,
        isAnyAdmin: isMatrizAdmin || isFranquiaAdmin || isContaAdmin,
        userRole: user?.role?.name,
        userScope: user?.scope
    };
};

/**
 * @desc Hook para verificar se o usuário é profissional
 */
export const useIsProfessional = () => {
    const { user } = useAuth();
    
    const isProfessional = !!(user?.profissional?.isProfissional);
    const isActiveProfessional = isProfessional && user?.profissional?.status === 'ativo' && user?.isActive;
    
    return {
        isProfessional,
        isActiveProfessional,
        profissionalData: user?.profissional,
        canManageSchedule: isActiveProfessional && (user?.permissions?.includes('agenda:view') || user?.permissions?.includes('agendamentos:view'))
    };
};

/**
 * @desc Hook para gerenciar conta selecionada (especialmente para Matriz Admin)
 */
export const useAccountSelector = () => {
    const { user, getAvailableAccounts, setSelectedAccountId, getSelectedAccountId } = useAuth();
    const { data: accounts = [] } = getAvailableAccounts;
    
    const isMatrizAdmin = user?.scope === 'matriz';
    const selectedAccountId = getSelectedAccountId();
    
    const selectAccount = (accountId) => {
        if (isMatrizAdmin && accountId) {
            setSelectedAccountId(accountId);
            return true;
        }
        return false;
    };
    
    const getSelectedAccount = () => {
        if (!selectedAccountId) return null;
        return accounts.find(acc => acc.account?._id === selectedAccountId);
    };
    
    return {
        isMatrizAdmin,
        accounts,
        selectedAccountId,
        selectedAccount: getSelectedAccount(),
        selectAccount,
        hasAccounts: accounts.length > 0
    };
};

export default useAuth;